import React from 'react';
import { Flex, VisuallyHidden, chakra } from '@chakra-ui/react';
import { ApplicantRow, CellProps, Row } from '../../types';
import { useLocation, useNavigate } from 'react-router-dom';

interface IRowEventsHandler {
  children?: React.ReactNode;
  rowProps?: Row | ApplicantRow;
  index?: number;
  isRowSelected?: boolean;
  gridColumnsWidth?: string;
}

export const CellEventsHandler = ({ children }: { children?: React.ReactNode; cellProps: CellProps }) => {
  return (
    <Flex height="100%" width="100%" flexDirection={'column'}>
      {children}
    </Flex>
  );
};

// Interface to extend RowEventsHandler props for Application drawer
interface IRowEventsHandlerWithDrawer extends IRowEventsHandler {
  openApplicationDrawer?: (row: Row) => void;
}

export const RowEventsHandler = (props: IRowEventsHandlerWithDrawer) => {
  if (props.rowProps && 'applicationId' in props.rowProps) {
    return <ApplicantDetailsRow {...props} />;
  }
  return <StudentRow {...props} />;
};

export const ApplicantDetailsRow = (props: IRowEventsHandler) => {
  const { children, rowProps, index, isRowSelected, gridColumnsWidth } = props;
  // Type guard to ensure we have an ApplicantRow
  if (!rowProps || !('applicationId' in rowProps)) {
    return <>{children}</>;
  }
  const { uni, applicationId, userId } = rowProps as ApplicantRow;
  const navigate = useNavigate();
  const location = useLocation();

  const onClickHandler = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    if (evt.preventDefault) {
      evt.preventDefault();
    }
    if (evt.stopPropagation) {
      evt.stopPropagation();
    }
    sessionStorage.setItem('cas-staff-uni-backUrl', location.search);
    navigate(`/application/${uni}/${userId}/${applicationId}/summary`);
  };

  return (
    <>
      <chakra.a
        href={`/application/${uni}/${userId}/${applicationId}`}
        role="row"
        tabIndex={0}
        aria-rowindex={index + 2}
        aria-selected={isRowSelected}
        onClick={onClickHandler}
        className={isRowSelected ? 'grid-Row selected' : 'grid-Row'}
        display="grid"
        height={24}
        gridTemplateColumns={gridColumnsWidth}
        bgColor="zebra-bg"
        _even={{ bgColor: 'chakra-subtle-bg' }}
        sx={{
          contain: 'layout style paint',
          '.namesContainer': {
            color: 'link-color',
          },
          _visited: {
            '.namesContainer': {
              color: 'link-visited-color',
            },
          },
          _hover: {
            bgColor: 'hover-bg',
            '.namesContainer': {
              color: 'link-hover-color',
            },
          },
          _focusVisible: {
            bgColor: 'hover-bg',
            '.grid-Cell:first-of-type': {
              bg: 'linear-gradient(to right, var(--ds-colors-link-color) 0, var(--ds-colors-link-color) 4px, transparent 4px, transparent 100%)',
              bgColor: 'inherit',
            },
          },
          _active: {
            bgColor: 'selected-bg',
            '.namesContainer': {
              color: 'link-active-color',
            },
          },
          '&.selected': {
            bgColor: 'selected-bg',
            '.namesContainer': {
              color: 'link-active-color',
            },
          },
        }}
      >
        {children}

        <VisuallyHidden role="gridcell">
          <span role="link" data-href={`/application/${uni}/${userId}/${applicationId}`}>
            Open student file
          </span>
        </VisuallyHidden>
      </chakra.a>
    </>
  );
};

export const StudentRow = (props: IRowEventsHandlerWithDrawer) => {
  const { children, rowProps, index, isRowSelected, gridColumnsWidth } = props;

  const [uni, studentNo, intakeId, offerId] = (rowProps as Row).p_key.split('#');

  const navigate = useNavigate();
  const location = useLocation();

  const onClickHandler = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    if (evt.preventDefault) {
      evt.preventDefault();
    }
    if (evt.stopPropagation) {
      evt.stopPropagation();
    }
    sessionStorage.setItem('cas-staff-uni-backUrl', location.search);
    navigate(`/student/${uni}/${studentNo}/${intakeId}/${offerId}`);
  };

  return (
    <>
      <chakra.a
        href={`/student/${uni}/${studentNo}/${intakeId}/${offerId}`}
        role="row"
        tabIndex={0}
        aria-rowindex={index + 2}
        aria-selected={isRowSelected}
        onClick={onClickHandler}
        className={isRowSelected ? 'grid-Row selected' : 'grid-Row'}
        display="grid"
        height={24}
        gridTemplateColumns={gridColumnsWidth}
        bgColor="zebra-bg"
        _even={{ bgColor: 'chakra-subtle-bg' }}
        sx={{
          contain: 'layout style paint',
          '.namesContainer': {
            color: 'link-color',
          },
          _visited: {
            '.namesContainer': {
              color: 'link-visited-color',
            },
          },
          _hover: {
            bgColor: 'hover-bg',
            '.namesContainer': {
              color: 'link-hover-color',
            },
          },
          _focusVisible: {
            bgColor: 'hover-bg',
            '.grid-Cell:first-of-type': {
              bg: 'linear-gradient(to right, var(--ds-colors-link-color) 0, var(--ds-colors-link-color) 4px, transparent 4px, transparent 100%)',
              bgColor: 'inherit',
            },
          },
          _active: {
            bgColor: 'selected-bg',
            '.namesContainer': {
              color: 'link-active-color',
            },
          },
          '&.selected': {
            bgColor: 'selected-bg',
            '.namesContainer': {
              color: 'link-active-color',
            },
          },
        }}
      >
        {children}

        <VisuallyHidden role="gridcell">
          <span role="link" data-href={`/student/${uni}/${studentNo}/${intakeId}/${offerId}`}>
            Open student file
          </span>
        </VisuallyHidden>
      </chakra.a>
    </>
  );
};
